import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Snackbar,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Checkbox
} from '@mui/material';
import {
  Assignment,
  CheckCircle,
  Cancel,
  ExpandMore,
  MedicalServices,
  Person,
  CalendarToday,
  AccessTime,
  Edit
} from '@mui/icons-material';
import { treatmentCoordinatorAPI, therapistAPI } from '../services/api';

function OPDTreatmentCoordinator() {
  const [treatments, setTreatments] = useState([]);
  const [stats, setStats] = useState(null);
  const [therapists, setTherapists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedTreatments, setSelectedTreatments] = useState([]);

  useEffect(() => {
    setSelectedTreatments([]);
  }, [statusFilter]);

  const [assignDialog, setAssignDialog] = useState({
    open: false,
    isBulk: false,
    prescribedTreatmentIds: [],
    prescribedTreatmentId: null,
    treatmentName: '',
    therapistId: '',
    scheduledDate: new Date().toISOString().split('T')[0],
    scheduledTime: '09:00',
    notes: ''
  });

  const [statusDialog, setStatusDialog] = useState({
    open: false,
    isBulk: false,
    assignmentIds: [],
    assignmentId: null,
    currentStatus: '',
    newStatus: '',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, [statusFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      const [treatmentsRes, statsRes, therapistsRes] = await Promise.all([
        treatmentCoordinatorAPI.getOPDTreatments(params),
        treatmentCoordinatorAPI.getOPDStatistics(),
        therapistAPI.getAll({ active: true })
      ]);

      setTreatments(treatmentsRes.data.data || []);
      setStats(statsRes.data.data || {});
      setTherapists(therapistsRes.data.data || []);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load OPD treatments');
    } finally {
      setLoading(false);
    }
  };

  const handleRowSelect = (treatment) => {
    setSelectedTreatments(prev =>
      prev.some(t => t.prescribed_treatment_id === treatment.prescribed_treatment_id)
        ? prev.filter(t => t.prescribed_treatment_id !== treatment.prescribed_treatment_id)
        : [...prev, treatment]
    );
  };

  const handleSelectAllClick = (date) => {
    const dateTreatments = groupedTreatments[date] || [];
    const allSelected = dateTreatments.every(t =>
      selectedTreatments.some(s => s.prescribed_treatment_id === t.prescribed_treatment_id)
    );

    if (allSelected) {
      setSelectedTreatments(prev =>
        prev.filter(s => !dateTreatments.some(t => t.prescribed_treatment_id === s.prescribed_treatment_id))
      );
    } else {
      setSelectedTreatments(prev => {
        const newSelections = [...prev];
        dateTreatments.forEach(t => {
          if (!newSelections.some(s => s.prescribed_treatment_id === t.prescribed_treatment_id)) {
            newSelections.push(t);
          }
        });
        return newSelections;
      });
    }
  };

  const handleBulkAssignOpen = () => {
    if (selectedTreatments.length === 0) return;
    const first = selectedTreatments[0];
    setAssignDialog({
      open: true,
      isBulk: true,
      prescribedTreatmentIds: selectedTreatments.map(t => t.prescribed_treatment_id),
      prescribedTreatmentId: null,
      treatmentName: `${selectedTreatments.length} Selected Treatments`,
      therapistId: first.therapist_id || '',
      scheduledDate: first.scheduled_date || new Date().toISOString().split('T')[0],
      scheduledTime: first.scheduled_time ? first.scheduled_time.substring(0, 5) : '09:00',
      notes: ''
    });
  };

  const handleBulkStatusOpen = () => {
    const assignmentIds = selectedTreatments
      .filter(t => t.assignment_id)
      .map(t => t.assignment_id);

    if (assignmentIds.length === 0) {
      setError('Please select assigned treatments to update status. Unassigned treatments must be assigned first.');
      return;
    }

    setStatusDialog({
      open: true,
      isBulk: true,
      assignmentIds,
      assignmentId: null,
      currentStatus: 'multiple',
      newStatus: '',
      notes: ''
    });
  };

  const handleOpenAssignDialog = (treatment) => {
    setAssignDialog({
      open: true,
      isBulk: false,
      prescribedTreatmentIds: [],
      prescribedTreatmentId: treatment.prescribed_treatment_id,
      treatmentName: treatment.treatment_name,
      therapistId: treatment.therapist_id || '',
      scheduledDate: treatment.scheduled_date || new Date().toISOString().split('T')[0],
      scheduledTime: treatment.scheduled_time ? treatment.scheduled_time.substring(0, 5) : '09:00',
      notes: treatment.assignment_notes || ''
    });
  };

  const handleCloseAssignDialog = () => {
    setAssignDialog({
      open: false,
      isBulk: false,
      prescribedTreatmentIds: [],
      prescribedTreatmentId: null,
      treatmentName: '',
      therapistId: '',
      scheduledDate: new Date().toISOString().split('T')[0],
      scheduledTime: '09:00',
      notes: ''
    });
  };

  const handleSubmitAssignment = async () => {
    if (!assignDialog.therapistId) {
      setError('Please select a therapist');
      return;
    }

    if (!assignDialog.scheduledDate || !assignDialog.scheduledTime) {
      setError('Please select date and time');
      return;
    }

    try {
      if (assignDialog.isBulk) {
        await treatmentCoordinatorAPI.bulkAssignOPD({
          prescribedTreatmentIds: assignDialog.prescribedTreatmentIds,
          therapistId: assignDialog.therapistId,
          scheduledDate: assignDialog.scheduledDate,
          scheduledTime: assignDialog.scheduledTime,
          notes: assignDialog.notes
        });
        setSuccess('Selected treatments assigned successfully');
        setSelectedTreatments([]);
      } else {
        await treatmentCoordinatorAPI.assignOPDTreatment(assignDialog.prescribedTreatmentId, {
          therapistId: assignDialog.therapistId,
          scheduledDate: assignDialog.scheduledDate,
          scheduledTime: assignDialog.scheduledTime,
          notes: assignDialog.notes
        });
        setSuccess('Treatment assigned successfully');
      }

      handleCloseAssignDialog();
      await loadData();
    } catch (err) {
      console.error('Failed to assign treatment:', err);
      setError(err.response?.data?.message || 'Failed to assign treatment');
    }
  };

  const handleOpenStatusDialog = (treatment) => {
    setStatusDialog({
      open: true,
      isBulk: false,
      assignmentIds: [],
      assignmentId: treatment.assignment_id,
      currentStatus: treatment.assignment_status || 'pending',
      newStatus: '',
      notes: treatment.assignment_notes || ''
    });
  };

  const handleCloseStatusDialog = () => {
    setStatusDialog({
      open: false,
      isBulk: false,
      assignmentIds: [],
      assignmentId: null,
      currentStatus: '',
      newStatus: '',
      notes: ''
    });
  };

  const handleSubmitStatusUpdate = async () => {
    if (!statusDialog.newStatus) {
      setError('Please select a new status');
      return;
    }

    try {
      if (statusDialog.isBulk) {
        await treatmentCoordinatorAPI.bulkUpdateOPDStatus({
          assignmentIds: statusDialog.assignmentIds,
          status: statusDialog.newStatus,
          notes: statusDialog.notes
        });
        setSuccess('Selected statuses updated successfully');
        setSelectedTreatments([]);
      } else {
        await treatmentCoordinatorAPI.updateOPDAssignmentStatus(statusDialog.assignmentId, {
          status: statusDialog.newStatus,
          notes: statusDialog.notes
        });
        setSuccess('Status updated successfully');
      }

      handleCloseStatusDialog();
      await loadData();
    } catch (err) {
      console.error('Failed to update status:', err);
      setError(err.response?.data?.message || 'Failed to update status');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'assigned':
        return 'info';
      case 'in_progress':
        return 'primary';
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const groupTreatmentsByDate = (treatments) => {
    const grouped = {};
    treatments.forEach(treatment => {
      const date = treatment.consultation_date.split('T')[0];
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(treatment);
    });
    return grouped;
  };

  const groupedTreatments = groupTreatmentsByDate(treatments);
  const sortedDates = Object.keys(groupedTreatments).sort().reverse();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      <Card elevation={2} sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <MedicalServices sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
            <Typography variant="h5" component="h2" fontWeight="bold">
              OPD Treatment Coordination
            </Typography>
          </Box>

          {/* Statistics */}
          {stats && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={2.4}>
                <Paper sx={{ p: 2, bgcolor: 'warning.50', textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight="bold" color="warning.dark">
                    {stats.unassigned_count || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Unassigned</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <Paper sx={{ p: 2, bgcolor: 'info.50', textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight="bold" color="info.dark">
                    {stats.assigned_count || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Assigned</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <Paper sx={{ p: 2, bgcolor: 'primary.50', textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight="bold" color="primary.dark">
                    {stats.in_progress_count || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">In Progress</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <Paper sx={{ p: 2, bgcolor: 'success.50', textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight="bold" color="success.dark">
                    {stats.completed_count || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Completed</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <Paper sx={{ p: 2, bgcolor: 'secondary.50', textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight="bold" color="secondary.dark">
                    {stats.today_consultations_count || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Today's Consultations</Typography>
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* Filter */}
          <Box sx={{ mb: 3 }}>
            <TextField
              select
              label="Filter by Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              size="small"
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="all">All Statuses</MenuItem>
              <MenuItem value="pending">Unassigned</MenuItem>
              <MenuItem value="assigned">Assigned</MenuItem>
              <MenuItem value="in_progress">In Progress</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </TextField>
          </Box>

          {selectedTreatments.length > 0 && (
            <Paper sx={{ p: 2, mb: 3, bgcolor: 'primary.50', border: 1, borderColor: 'primary.light', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Typography variant="body2" fontWeight="bold">
                {selectedTreatments.length} OPD treatment(s) selected
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<Assignment />}
                  onClick={handleBulkAssignOpen}
                >
                  Assign Selected
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  startIcon={<CheckCircle />}
                  onClick={handleBulkStatusOpen}
                >
                  Update Status
                </Button>
              </Box>
            </Paper>
          )}

          {/* Treatments List */}
          {treatments.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
              <Typography color="text.secondary">
                No OPD treatments found
              </Typography>
            </Paper>
          ) : (
            <Box>
              {sortedDates.map(date => {
                const dateTreatments = groupedTreatments[date] || [];
                const allSelected = dateTreatments.length > 0 && dateTreatments.every(t =>
                  selectedTreatments.some(s => s.prescribed_treatment_id === t.prescribed_treatment_id)
                );
                const someSelected = dateTreatments.length > 0 && dateTreatments.some(t =>
                  selectedTreatments.some(s => s.prescribed_treatment_id === t.prescribed_treatment_id)
                ) && !allSelected;

                return (
                  <Accordion key={date} defaultExpanded={sortedDates.indexOf(date) === 0}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        <CalendarToday color="primary" />
                        <Typography variant="h6" fontWeight="bold">
                          {new Date(date).toLocaleDateString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </Typography>
                        <Chip
                          label={`${groupedTreatments[date].length} Treatment${groupedTreatments[date].length > 1 ? 's' : ''}`}
                          size="small"
                          color="primary"
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: 'grey.50' }}>
                              <TableCell style={{ width: '50px' }}>
                                <Checkbox
                                  indeterminate={someSelected}
                                  checked={allSelected}
                                  onChange={() => handleSelectAllClick(date)}
                                />
                              </TableCell>
                              <TableCell><strong>Patient</strong></TableCell>
                              <TableCell><strong>Treatment</strong></TableCell>
                              <TableCell><strong>Doctor</strong></TableCell>
                              <TableCell><strong>Therapist</strong></TableCell>
                              <TableCell><strong>Scheduled</strong></TableCell>
                              <TableCell><strong>Status</strong></TableCell>
                              <TableCell align="center"><strong>Actions</strong></TableCell>
                            </TableRow>
                          </TableHead>
                        <TableBody>
                          {groupedTreatments[date].map((treatment) => {
                            const isSelected = selectedTreatments.some(s => s.prescribed_treatment_id === treatment.prescribed_treatment_id);
                            return (
                              <TableRow key={treatment.prescribed_treatment_id} hover selected={isSelected}>
                                <TableCell>
                                  <Checkbox
                                    checked={isSelected}
                                    onChange={() => handleRowSelect(treatment)}
                                  />
                                </TableCell>
                                <TableCell>
                                <Typography variant="body2" fontWeight="medium">
                                  {treatment.first_name} {treatment.last_name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {treatment.patient_code}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">
                                  {treatment.treatment_name}
                                </Typography>
                                {treatment.treatment_description && (
                                  <Typography variant="caption" color="text.secondary">
                                    {treatment.treatment_description.substring(0, 50)}
                                    {treatment.treatment_description.length > 50 ? '...' : ''}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {treatment.doctor_name}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {treatment.therapist_name ? (
                                  <Box>
                                    <Typography variant="body2" fontWeight="medium">
                                      {treatment.therapist_name}
                                    </Typography>
                                    {treatment.therapist_specialization && (
                                      <Typography variant="caption" color="text.secondary">
                                        {treatment.therapist_specialization}
                                      </Typography>
                                    )}
                                  </Box>
                                ) : (
                                  <Typography variant="body2" color="text.secondary" fontStyle="italic">
                                    Not assigned
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                {treatment.scheduled_date && treatment.scheduled_time ? (
                                  <Box>
                                    <Typography variant="body2">
                                      {new Date(treatment.scheduled_date).toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric'
                                      })}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {treatment.scheduled_time.substring(0, 5)}
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Typography variant="body2" color="text.secondary" fontStyle="italic">
                                    Not scheduled
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={treatment.assignment_status || 'pending'}
                                  color={getStatusColor(treatment.assignment_status)}
                                  size="small"
                                  sx={{ textTransform: 'capitalize' }}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                  <Tooltip title={treatment.assignment_id ? 'Edit Assignment' : 'Assign Treatment'}>
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() => handleOpenAssignDialog(treatment)}
                                    >
                                      {treatment.assignment_id ? <Edit /> : <Assignment />}
                                    </IconButton>
                                  </Tooltip>
                                  {treatment.assignment_id && (
                                    <Tooltip title="Update Status">
                                      <IconButton
                                        size="small"
                                        color="success"
                                        onClick={() => handleOpenStatusDialog(treatment)}
                                      >
                                        <CheckCircle />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          )})}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              );
            })}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Assignment Dialog */}
      <Dialog open={assignDialog.open} onClose={handleCloseAssignDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Assignment color="primary" />
            Assign Treatment
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Treatment
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {assignDialog.treatmentName}
              </Typography>
              <Divider sx={{ my: 2 }} />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Select Therapist *"
                value={assignDialog.therapistId}
                onChange={(e) => setAssignDialog({ ...assignDialog, therapistId: e.target.value })}
                required
              >
                <MenuItem value="">Select Therapist</MenuItem>
                {therapists.map((therapist) => (
                  <MenuItem key={therapist.id} value={therapist.id}>
                    {therapist.first_name} {therapist.last_name} - {therapist.specialization}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Scheduled Date *"
                value={assignDialog.scheduledDate}
                onChange={(e) => setAssignDialog({ ...assignDialog, scheduledDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="time"
                label="Scheduled Time *"
                value={assignDialog.scheduledTime}
                onChange={(e) => setAssignDialog({ ...assignDialog, scheduledTime: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes"
                value={assignDialog.notes}
                onChange={(e) => setAssignDialog({ ...assignDialog, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAssignDialog} startIcon={<Cancel />}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitAssignment}
            variant="contained"
            startIcon={<Assignment />}
          >
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog open={statusDialog.open} onClose={handleCloseStatusDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircle color="success" />
            Update Treatment Status
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Current Status
              </Typography>
              <Chip
                label={statusDialog.currentStatus || 'pending'}
                color={getStatusColor(statusDialog.currentStatus)}
                sx={{ textTransform: 'capitalize' }}
              />
              <Divider sx={{ my: 2 }} />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="New Status *"
                value={statusDialog.newStatus}
                onChange={(e) => setStatusDialog({ ...statusDialog, newStatus: e.target.value })}
                required
              >
                <MenuItem value="">Select Status</MenuItem>
                <MenuItem value="assigned">Assigned</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes"
                value={statusDialog.notes}
                onChange={(e) => setStatusDialog({ ...statusDialog, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseStatusDialog} startIcon={<Cancel />}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitStatusUpdate}
            variant="contained"
            color="success"
            startIcon={<CheckCircle />}
          >
            Update Status
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default OPDTreatmentCoordinator;
